import { InstagramMessageModel } from "@jugl-web/rest-api/integrations/models/InstagramMessage/InstagramMessageModel";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import React, { useCallback, useMemo } from "react";
import {
  ChatMessageAttachmentType,
  ChatMessagePayloadAttachment,
  CRMChannel,
  InstagramMessageType,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { ChatMessageBubbleContextValue } from "@web-src/modules/chats/components/CommonMessageBubble/providers/ChatMessageBubbleProvider/ChatMessageBubbleContext";
import { ChatMessageBubbleAction } from "@web-src/modules/chats/components/CommonMessageBubble/types/ChatMessageBubbleAction";
import { CHAT_MESSAGE_PAYLOAD_VERSION } from "@jugl-web/utils/consts";
import { CommonMessageBubble } from "@web-src/modules/chats/components/CommonMessageBubble";
import { ChatMessageReplyInfo } from "@web-src/modules/chats/components/CommonMessageBubble/types/ChatMessageReplyInfo";
import { tokenizeMessageBody } from "@web-src/modules/chats/utils";
import { instagramMessageToAttachmentsPayload } from "@web-src/modules/client-support/utils/instagramMessageToAttachmentsPayload";
import { useSelector } from "react-redux";
import { selectUserId } from "@web-src/modules/auth/store/authSlice";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import { isInstagramMessageSupported } from "../../../../utils/isInstagramMessageSupported";
import { useGetSenderDetails } from "../../hooks/useGetSenderDetails";
import { useClientSupportPageContext } from "../../../../providers/ClientSupportPageProvider/useClientSupportPageContext";
import { isInstagramMessageDeleted } from "../../../../utils/isInstagramMessageDeleted";

export type InstagramMessageBubbleProps = {
  message: InstagramMessageModel;
  readOnly?: boolean;
  isSelectable?: boolean;
  isSelected?: boolean;
  isHighlighted?: boolean;
  isPreview?: boolean;
  onSelect?: (messageId: string, isSelected: boolean) => void;
  onTriggerAction?: (
    action: ChatMessageBubbleAction,
    message: InstagramMessageModel
  ) => void;
  onReplyPreviewClick?: (replyInfo: ChatMessageReplyInfo) => void;
  onReaction?: (reaction: string) => void;
  isFirstUnread?: boolean;
  isMessageActionsDisabled?: boolean;
  groupInfo?: {
    isFirst?: boolean;
    isLast?: boolean;
  };
};

export const InstagramMessageBubble: React.FC<InstagramMessageBubbleProps> = ({
  message,
  readOnly,
  isSelectable,
  isSelected,
  onSelect,
  onReaction,
  onTriggerAction,
  onReplyPreviewClick,
  groupInfo,
  isHighlighted,
  isPreview,
  isMessageActionsDisabled,
}) => {
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const { isCx } = useAppVariant();
  const { getSenderDetails } = useGetSenderDetails();
  const { setOpenedUserProfile } = useClientSupportPageContext();
  const meId = useSelector(selectUserId);
  const { t } = useTranslations();

  const { data: replyMessage } =
    integrationsApi.useGetInstagramMessageByIdQuery(
      "message" in message.payload &&
        "reply_to" in message.payload.message &&
        message.payload.message.reply_to
        ? { entityId, params: { msg_id: message.payload.message.reply_to.mid } }
        : skipToken
    );

  const replyInfo = useMemo(() => {
    const reply = replyMessage?.data[0];
    if (reply && "msg_type" in reply) {
      let replyMsgBody = t({
        id: "client-support-page.unsupported-message-format",
        defaultMessage: "Unsupported message format",
      });
      if (isInstagramMessageDeleted(reply)) {
        replyMsgBody = t({
          id: "client-support-page.message-has-been-deleted",
          defaultMessage: "Message has been deleted",
        });
      } else if (isInstagramMessageSupported(reply)) {
        if (reply.msg_type === InstagramMessageType.text) {
          replyMsgBody = reply.payload.message.text;
        } else if (reply.msg_type === InstagramMessageType.image) {
          replyMsgBody = t({ id: "common.image", defaultMessage: "Image" });
        } else if (reply.msg_type === InstagramMessageType.video) {
          replyMsgBody = t({ id: "common.video", defaultMessage: "Video" });
        } else if (reply.msg_type === InstagramMessageType.audio) {
          replyMsgBody = t({ id: "common.audio", defaultMessage: "Audio" });
        }
      }

      const replyAttachments = instagramMessageToAttachmentsPayload({
        message: reply,
        entityId,
        t,
      });

      return {
        replyMsgId: reply.msg_id,
        replyMsgBody,
        replyMsgUser: reply.from,
        replyUserName: getSenderDetails(reply).senderName,
        replyAttachments,
      };
    }

    return undefined;
  }, [entityId, getSenderDetails, replyMessage?.data, t]);

  const instagramReactionEmojiMap: Record<string, string> = useMemo(
    () => ({
      love: "❤️",
      laugh: "😂",
      wow: "😮",
      sad: "😢",
      angry: "😡",
      like: "👍",
    }),
    []
  );

  const isUnsupported = useMemo(
    () => !isInstagramMessageSupported(message),
    [message]
  );

  const isDeleted = useMemo(
    () => isInstagramMessageDeleted(message),
    [message]
  );

  const attachments = useMemo(
    () =>
      instagramMessageToAttachmentsPayload({ message, entityId, t }) ||
      undefined,
    [entityId, message, t]
  );

  // Get message text body
  const messageTextBody = useMemo(() => {
    if (!("msg_type" in message) || !("message" in message.payload)) return "";
    switch (message.msg_type) {
      case InstagramMessageType.text:
        return message.payload.message.text || "";
      default:
        return "";
    }
  }, [message]);

  const previewUrl = useMemo(() => {
    if (!messageTextBody) return undefined;
    return tokenizeMessageBody(messageTextBody).find(
      (item) => item.type === "url"
    )?.rawString;
  }, [messageTextBody]);

  const handleTriggerAction = useCallback(
    (action: ChatMessageBubbleAction) => {
      onTriggerAction?.(action, message);
    },
    [message, onTriggerAction]
  );

  const handleTriggerReaction = useCallback(
    (reactionEmoji: string) => {
      const reactionName = Object.entries(instagramReactionEmojiMap).find(
        ([, value]) => value === reactionEmoji
      )?.[0];

      if (!reactionName) return;

      onReaction?.(reactionName);
    },
    [onReaction, instagramReactionEmojiMap]
  );

  const handleAvatarClick = useCallback(
    (userId: string) => {
      setOpenedUserProfile(userId);
    },
    [setOpenedUserProfile]
  );

  const isOutgoing = message.type === "outgoing";

  const chatMessageBubbleProps: ChatMessageBubbleContextValue = useMemo(() => {
    const availableActions = [];

    if (!isUnsupported && !isDeleted) {
      if (message.type === "incoming") {
        availableActions.push(ChatMessageBubbleAction.REACTION);
      }

      if (messageTextBody) {
        availableActions.push(ChatMessageBubbleAction.COPY);
      }
    }
    return {
      messageId: message.msg_id,
      isDeleted,
      isUnsupported,
      isForwarded: false,
      messageClientId: message.id,
      messageFrom: message.from,
      isOutgoing,
      isGroupChat: true,
      showOutgoingSenderDetails: true,
      readOnly: !!readOnly,
      entityId,
      messageTextBody,
      attachments,
      isMediaMessageOnly:
        !!attachments?.length &&
        attachments.every((attachment) =>
          [
            ChatMessageAttachmentType.image,
            ChatMessageAttachmentType.video,
          ].includes(attachment.type)
        ),
      // NOTE: always use the latest payload version for whatsapp as it's static
      messagePayloadVersion: CHAT_MESSAGE_PAYLOAD_VERSION,
      isSelectable,
      isSelected,
      isMessageActionsDisabled,
      onReplyPreviewClick,
      onSelect,
      getAttachmentUrls: (a: ChatMessagePayloadAttachment) => ({
        preview: a.url || "",
        stream: a.url || "",
      }),
      customReactions: [instagramReactionEmojiMap.love],
      reactions:
        message.payload.reaction &&
        instagramReactionEmojiMap[message.payload.reaction.reaction]
          ? {
              summary: [
                {
                  reaction:
                    instagramReactionEmojiMap[
                      message.payload.reaction.reaction
                    ],
                  users: [message.payload.reaction.user_id],
                  count: 1,
                },
              ],
              myReactions:
                message.payload.reaction.user_id === meId
                  ? [
                      instagramReactionEmojiMap[
                        message.payload.reaction.reaction
                      ],
                    ]
                  : [],
            }
          : undefined,
      onAvatarClick: !isCx ? handleAvatarClick : undefined,
      replyInfo,
      groupInfo,
      isHighlighted,
      isPreview,
      isEdited: !!message.payload.edited,
      outgoingStatus: isOutgoing ? (message.read ? "read" : "sent") : undefined,
      timestamp: message.inserted_at,
      availableActions,
      triggerAction: handleTriggerAction,
      triggerReaction: handleTriggerReaction,
      isReactionDetailsDisabled: true,
      previewUrl,
      ...getSenderDetails(message, CRMChannel.instagram),
    };
  }, [
    isUnsupported,
    isDeleted,
    message,
    isOutgoing,
    readOnly,
    entityId,
    messageTextBody,
    attachments,
    isSelectable,
    isSelected,
    isMessageActionsDisabled,
    onReplyPreviewClick,
    onSelect,
    instagramReactionEmojiMap,
    meId,
    isCx,
    handleAvatarClick,
    replyInfo,
    groupInfo,
    isHighlighted,
    isPreview,
    handleTriggerAction,
    handleTriggerReaction,
    previewUrl,
    getSenderDetails,
  ]);

  return <CommonMessageBubble {...chatMessageBubbleProps} />;
};
